import { useEffect, useRef, useCallback } from 'react';
import { registerModal, unregisterModal, unregisterModalSilent, wasClosedByPopstate } from '../utils/modalManager';

/**
 * Handles hardware back button for modals on mobile.
 * Uses centralized modalManager to prevent cascading closes.
 *
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {function} onClose - Callback to close the modal
 * @param {string} label - Debug label to identify this modal in logs
 * @returns {{ closeSilent: () => void }} closeSilent — close without history.back()
 */
export default function useModalBack(isOpen, onClose, label = '') {
  const idRef = useRef(null);
  const silentRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (isOpen && !idRef.current) {
      // Modal just opened — register with centralized manager
      silentRef.current = false;
      idRef.current = registerModal(() => onCloseRef.current(), label);
    } else if (!isOpen && idRef.current) {
      // Modal closed via UI — unregister (skip if closed by popstate)
      const id = idRef.current;
      idRef.current = null;
      if (!wasClosedByPopstate(id)) {
        if (silentRef.current) {
          unregisterModalSilent(id);
        } else {
          unregisterModal(id);
        }
      }
      silentRef.current = false;
    }
  }, [isOpen]);

  // Close without triggering history.back() — for use with setSearchParams(replace:true)
  const closeSilent = useCallback(() => {
    silentRef.current = true;
    onCloseRef.current();
  }, []);

  // Cleanup on unmount (in case component unmounts while still open)
  // Use silent unregister — if unmounting due to navigation, history.back() would undo it
  useEffect(() => {
    return () => {
      if (idRef.current) {
        unregisterModalSilent(idRef.current);
        idRef.current = null;
      }
    };
  }, []);

  return { closeSilent };
}
